import {RefObject, useState, useRef, useCallback} from 'react';
import {useIsomorphicLayoutEffect} from 'core/hooks/useIsomorficLayoutEffect';

type TUsePixelOverflow = {
    mainRef: RefObject<HTMLElement>;
    mainInnerRef: RefObject<HTMLElement>;
};

const OVERFLOW_CALCULATION_TIMEOUT = 400;

export const usePixelOverflow = (params: TUsePixelOverflow) => {
    const {mainRef, mainInnerRef} = params;
    const [pixelOverflow, setPixelOverflow] = useState(0);
    const rafIdRef = useRef<number>();
    const timeoutRef = useRef<NodeJS.Timeout>();

    const calculateOverflow = useCallback(() => {
        const main = mainRef.current;
        const mainInner = mainInnerRef.current;

        if (!main || !mainInner) {
            return;
        }

        const styles = getComputedStyle(main);
        const height = main.offsetHeight;
        const paddingTop = parseFloat(styles.paddingTop);
        const paddingBottom = parseFloat(styles.paddingBottom);
        const mainHeight = height - paddingTop - paddingBottom;

        const stylesInner = getComputedStyle(mainInner);
        const heightInner = mainInner.scrollHeight;
        const paddingTopInner = parseFloat(stylesInner.paddingTop);
        const paddingBottomInner = parseFloat(stylesInner.paddingBottom);
        const mainInnerHeight = heightInner - paddingTopInner - paddingBottomInner;

        const overflow = Math.max(0, mainInnerHeight - mainHeight);

        setPixelOverflow(overflow);
    }, [mainRef, mainInnerRef]);

    useIsomorphicLayoutEffect(() => {
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        rafIdRef.current = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                timeoutRef.current = setTimeout(() => {
                    calculateOverflow();
                }, OVERFLOW_CALCULATION_TIMEOUT);
            });
        });

        return () => {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [calculateOverflow]);

    return {pixelOverflow};
};
